
import { Component, Input, AfterViewInit, OnChanges, OnDestroy, SimpleChanges, ViewChild, ElementRef, NgZone, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { HttpClient } from '@angular/common/http';
import * as L from 'leaflet';
import { OrganizationUtilService } from '../../../core/services/organization-util.service';
import _ from 'lodash';
import { DataService } from '../../../core/services/data.service';

@Component({
  selector: 'app-create-update-dialog',
  standalone: true,
  imports: [FormsModule],
  templateUrl: './create-update-dialog.component.html',
  styleUrl: './create-update-dialog.component.css'
})
export class CreateUpdateDialogComponent implements AfterViewInit, OnChanges, OnDestroy, OnInit {
  @Input() data: any = {
    name: '',
    lat: null,
    lon: null,
    isInsideBuilding: false,
    buildings: [],
    selectedBuilding: null,
    selectedFloor: null,
    clickedPerimeter: null
  };
  files: File[] = [];
  errorMessage = '';
  showErrorMessage = false;
  submittingUpdate = false;

  private floorMap: L.Map | null = null;
  private clickedCircle: L.CircleMarker | null = null;
  private floorLayers: L.Layer[] = [];

  constructor(public activeModal: NgbActiveModal, private ngZone: NgZone,
    private organizationutilService: OrganizationUtilService,
    private dataService: DataService) { }
  ngOnInit(): void {
    this.setFloor();
  }
  setFloor() {
    this.data.isInsideBuilding = !!this.data.isInsideBuilding;
    this.data.buildings = this.organizationutilService.data.myBuildings || [];

    const incomingFloor = this.data.selectedFloor ?? this.data.floor;
    if (incomingFloor) {
      const buildings = _.filter(this.data.buildings, (o) => o.perimeterId === incomingFloor.building?.perimeterId);
      if (buildings && buildings.length > 0) {
        this.data.selectedBuilding = buildings[0];
        const floorList = Array.isArray(this.data.selectedBuilding.floorPerimeters)
          ? this.data.selectedBuilding.floorPerimeters
          : _.toArray(this.data.selectedBuilding.floorPerimeters || []);

        const match = floorList.find((fp: any) => {
          return String(fp.id ?? fp.perimeterId ?? fp.arcAngelId) === String(incomingFloor.id ?? incomingFloor.perimeterId ?? incomingFloor.arcAngelId);
        });

        this.data.selectedFloor = match ?? incomingFloor;
      } else {
        this.data.selectedFloor = incomingFloor;
      }
    }

    setTimeout(() => {
      this.createFloorMap();
    }, 250);
  }
  ngAfterViewInit(): void {
    if (this.data && this.data.selectedFloor) {
      setTimeout(() => this.createFloorMap(), 100);
    }
  }
  comparePerimeter = (o1: any, o2: any): boolean => {
    if (o1 === o2) return true;
    if (!o1 || !o2) return false;
    const id1 = String(o1.id ?? o1.perimeterId ?? o1.arcAngelId ?? '');
    const id2 = String(o2.id ?? o2.perimeterId ?? o2.arcAngelId ?? '');
    return id1 !== '' && id1 === id2;
  };
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] && !changes['data'].firstChange) {
      setTimeout(() => this.createFloorMap(), 100);
    }
  }

  ngOnDestroy(): void {
    if (this.floorMap) {
      this.floorMap.off();
      this.floorMap.remove();
      this.floorMap = null;
    }
  }

  onFileChange(event: any) {
    this.files = Array.from(event.target.files);
  }

  isInsideBuildingClicked() {
    this.data.clickedPerimeter = null;
  }

  floorChanged() {
    this.createFloorMap();
  }

  private async createFloorMap() {
    if (this.floorMap) {
      this.floorMap.off();
      this.floorMap.remove();
      this.floorMap = null;
      this.floorLayers = [];
      this.clickedCircle = null;
    }

    const floor = this.data?.selectedFloor;
    if (!floor) { return; }

    this.floorMap = L.map('new-update-map', {
      center: [floor.centerpoint.latitude, floor.centerpoint.longitude],
      zoom: 19,
      scrollWheelZoom: false,
      doubleClickZoom: false
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { opacity: 0.25 }).addTo(this.floorMap);

    const floorLayer = this.addPolygonToMap(floor, floor.name || 'floor', this.floorMap!);
    (floorLayer as any).perimeter = floor;
    floorLayer.on('click', (e: any) => this.onMapClick(e, floorLayer));

    const perimetersArray = _.toArray(floor.perimeters || []);
    perimetersArray.forEach((p: any) => {
      const layer = this.addPolygonToMap(p, p.name, this.floorMap!);
      (layer as any).perimeter = p;
      layer.on('click', (e: any) => this.onMapClick(e, layer));
    });

    this.floorMap.on('click', (e: any) => this.onMapClick(e, null));

    if (floor.floorplanId) {
      try {
        const arrBuff = await this.fetchFloorplanFile(floor.floorplanId);
        if (arrBuff && floor.getFloorPlanRotationCoords) {
          const rot = floor.getFloorPlanRotationCoords();
          if (rot && rot.length >= 3) {
            const point1 = this.flipCoords(rot[0]);
            const point2 = this.flipCoords(rot[1]);
            const point3 = this.flipCoords(rot[2]);
            const imgSrc = 'data:image/png;base64,' + this._arrayBufferToBase64(arrBuff);
            try {
              (L as any).imageOverlay.rotated(imgSrc, point1, point2, point3, { opacity: 1.0 }).addTo(this.floorMap!);
            } catch (err) {
              const bounds = (floor.locations || []).map((p: any) => [p.latitude ?? p.lat, p.longitude ?? p.lon]);
              if (bounds.length) {
                L.imageOverlay(imgSrc, bounds).addTo(this.floorMap!);
              }
            }
          }
        }
      } catch (e) {
      }
    }

    if (floor.locations && floor.locations.length) {
      const latlngs = floor.locations.map((p: any) => [p.latitude ?? p.lat, p.longitude ?? p.lon]);
      try { this.floorMap.fitBounds(latlngs as any); } catch (e) { }
    }
  }
  addPolygonToMap(
    polygonData: any,
    polygonName: string,
    map: L.Map,
    date?: any
  ): L.Polygon {
    // Helper to convert RGB to hex
    function rgbToHex(r: number, g: number, b: number): string {
      return (
        '#' +
        [r, g, b]
          .map((x) => {
            const hex = Math.round(x).toString(16);
            return hex.length === 1 ? '0' + hex : hex;
          })
          .join('')
      );
    }

    let outlineColor: string | undefined;
    let strokeWeight: number | undefined;

    if (polygonData.outlineColor) {
      outlineColor = rgbToHex(
        polygonData.outlineColor.red * 255,
        polygonData.outlineColor.green * 255,
        polygonData.outlineColor.blue * 255
      );
    }

    if (polygonData.outlineWidth) {
      strokeWeight = polygonData.outlineWidth;
    }

    const fillColor = rgbToHex(
      polygonData.material.red * 255,
      polygonData.material.green * 255,
      polygonData.material.blue * 255
    );
    let fillOpacity = polygonData.material.alpha;

    if (polygonData.layerPath === 'ArcAngel//Perimeters') {
      fillOpacity = 0.5;
    }

    // Ensure locations is an array of [lat, lng] pairs
    const locations = polygonData.locations;

    const leafletPolygon = L.polygon(locations, {
      color: outlineColor,
      weight: strokeWeight,
      fillColor: fillColor,
      opacity: fillOpacity,
    });

    leafletPolygon.addTo(map).bindPopup(polygonName);
    (leafletPolygon as any).arcAngelUpdatedDate = date;

    return leafletPolygon;
  }
  private onMapClick(e: any, targetLayer: any | null) {
    // run inside Angular zone to update bindings
    this.ngZone.run(() => {
      const lat = e.latlng.lat;
      const lon = e.latlng.lng;
      this.data.lat = lat;
      this.data.lon = lon;
      this.data.clickedPerimeter = targetLayer ? (targetLayer as any).perimeter : null;

      if (this.clickedCircle && this.floorMap) {
        this.floorMap.removeLayer(this.clickedCircle);
      }
      this.clickedCircle = L.circleMarker([lat, lon], {
        radius: 5,
        color: '#ffffff',
        weight: 1,
        fill: true,
        fillColor: '#000000',
        fillOpacity: 1
      }).addTo(this.floorMap!);
    });
  }


  private fetchFloorplanFile(floorplanId: string): Promise<any> {
    return Promise.resolve(null);
  }

  private flipCoords(point: any) {
    // original flip swapped [lon,lat] -> [lat,lon]
    return [point[1], point[0]];
  }

  private _arrayBufferToBase64(buffer: ArrayBuffer) {
    let binary = '';
    const bytes = new Uint8Array(buffer);
    const len = bytes.byteLength;
    for (let i = 0; i < len; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  send() {
    this.showErrorMessage = false;
    let perimeterId = this.data.clickedPerimeter?.arcAngelId;

    if (!this.data.name || this.data.name.length === 0) {
      this.showError('Please add a message to the update.');
    } else if (this.data.isInsideBuilding && !this.data.clickedPerimeter) {
      this.showError('Please click on floor to pick a location.');
    } else {
      this.submittingUpdate = true;
      const file = this.files && this.files.length ? this.files[0] : undefined;
      this.dataService.createUpdate(this.data.eventId, this.data.name, this.data.lat, this.data.lon, file, perimeterId)
        .subscribe({
          next: () => {
            this.submittingUpdate = false;
            this.cancel();
          },
          error: (err) => {
            console.error('Error creating update', err);
            this.submittingUpdate = false;
            this.showError(file ? 'Could not upload attachment.' : 'Could not create update.');
          }
        });
    }
  }

  cancel() {
    this.activeModal.close('Alert incident');
  }

  showError(error: string) {
    this.submittingUpdate = false;
    this.errorMessage = error;
    this.showErrorMessage = true;
  }
}